import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  CodingModel,
  CreateGuaranteeRequest,
  DiagnosisAuthorType,
  DiagnosisChipView,
  DiagnosisRole,
  DiagnosisWriteModel,
  GuaranteeDecision,
  PrescriptionWriteModel,
  UpdateGuaranteeRequest,
} from '@lib/guarantees/data-access';
import { createGuarantee, resolveGuarantee, updateGuarantee } from '@lib/guarantees/data-access/store/guarantees-v2.actions';
import { selectGuaranteesSaving, selectLiveRefreshContext, selectTimelineVisits } from '@lib/guarantees/data-access/store/guarantees-v2.selectors';
import { ChipComponent } from '@lib/guarantees/feature/components/common/chip.component';
import { DiagnosisChipComponent, DiagnosisSummary } from '@lib/guarantees/feature/components/common/diagnosis-chip.component';
import { DECISION_ICON, DECISION_LABEL_KEY, SERVICE_KIND_ICON, SERVICE_KIND_LABEL_KEY } from '@lib/guarantees/feature/components/common/guarantee-vocabulary';
import {
  DECISION_ANSWERS,
  DECISION_BLOCKER_HINT_KEY,
  DecisionAnswer,
  DecisionBlocker,
  DecisionDraft,
  allowsGrant,
  hasCategories,
  toDecisionWriteModel,
} from '@lib/guarantees/feature/components/common/prescription-decisions';
import { Icd10PickerComponent } from '@lib/guarantees/feature/components/icd10-picker/icd10-picker.component';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { CalloutComponent } from '../components/common/callout.component';
import { CheckBoxComponent } from '../components/common/check-box.component';
import { ControlDirective } from '../components/common/control.directive';
import { FieldLabelComponent } from '../components/common/field-label.component';
import { OptionPickerComponent, PickerOption } from '../components/common/option-picker.component';
import { StepNavComponent, StepNavEntry } from '../components/common/step-nav.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { DIALOG_CLASSES } from '../components/ui/ui.classes';
import { AppointmentChoice, AppointmentPickerComponent } from './appointment-picker.component';
import { DECLINE_REASONS, GUARANTEE_REASONS } from './decision-reasons';
import { PRESCRIPTION_FORM_DIALOG_CONFIG, PrescriptionFormComponent, PrescriptionFormData, PrescriptionFormResult } from './prescription-form.component';

/** A line queued for the create, together with whatever ruling it is being given in the same pass. */
export interface QueuedLine {
  prescription: PrescriptionWriteModel;
  /** Unset leaves the line undecided, which is where a line starts when nobody rules on it. */
  decision?: DecisionDraft;
}

/** A queued line with its place in the queue, so a grouped reading still addresses the flat list. */
export interface QueuedGroupEntry {
  index: number;
  queued: QueuedLine;
}

/**
 * The queued lines under the diagnosis each of them answers — the same split the care plan reads a
 * stored guarantee by, so what a line is being guaranteed against is visible while it is authored
 * rather than only after the record exists.
 */
export interface QueuedGroup {
  key: string;
  /** The diagnosis these lines answer. Null for the group holding those with none behind them. */
  diagnosis: DiagnosisSummary | null;
  entries: QueuedGroupEntry[];
}

/** A diagnosis as it is being authored: the code picked, and what it is to this guarantee. */
export interface DiagnosisDraft {
  coding: CodingModel;
  /** Primary from the moment the code is added; demoted in place where the manager says otherwise. */
  role: DiagnosisRole;
}

export interface GuaranteeFormValue {
  appointmentId?: string;
  description?: string;
  /** Derived on create, CARRIED on amend: re-deriving turns a request into a doctor's prescription. */
  isServiceRequest?: boolean;
  /** Never asked, always carried: the update replaces the whole header, so omitting it clears it. */
  isLiloStaff?: boolean;
}

/** Either a record to write, or a guarantee to open when the chosen visit is already answered. */
export type GuaranteeFormResult = { kind: 'submit'; request: CreateGuaranteeRequest | UpdateGuaranteeRequest } | { kind: 'open'; guaranteeId: string };

export interface GuaranteeFormData {
  mode: 'create' | 'edit';
  /** The member whose guarantee this is — the appointments offered in step one are theirs. */
  memberId: string;
  /** The record being amended. Absent on create: there is nothing to name yet. */
  guaranteeId?: string;
  initial?: GuaranteeFormValue;
  /** How many lines the guarantee already carries, which is what step three reports on an edit. */
  prescriptionCount?: number;
}

/**
 * Opens the form and carries its answer into the store. Held beside the form rather than in each
 * caller: the create button and the card's edit button must dispatch the same way.
 */
export function openGuaranteeForm(dialog: MatDialog, store: Store, data: GuaranteeFormData): void {
  dialog
    .open<GuaranteeFormComponent, GuaranteeFormData, GuaranteeFormResult>(GuaranteeFormComponent, {
      width: '90vw',
      maxWidth: '560px',
      maxHeight: '90vh',
      autoFocus: false,
      // Named by the heading it already shows. Without this the dialog announces as unlabelled, and
      // nothing but its position in the overlay stack tells it apart from the line form above it.
      ariaLabelledBy: 'guarantee-form-title',
      data,
    })
    .afterClosed()
    .pipe(take(1))
    .subscribe(result => {
      if (!result) {
        return;
      }

      // The visit turned out to be answered already, so the form hands over to what answered it.
      if (result.kind === 'open') {
        store.dispatch(resolveGuarantee({ guaranteeId: result.guaranteeId, track: true }));

        return;
      }

      if (data.mode === 'create') {
        store.dispatch(createGuarantee({ request: result.request as CreateGuaranteeRequest }));
      } else if (data.guaranteeId) {
        store.dispatch(updateGuarantee({ guaranteeId: data.guaranteeId, request: result.request }));
      }
    });
}

/** The three steps, in the order a guarantee is actually assembled. */
export const FORM_STEPS = ['visit', 'details', 'prescriptions'] as const;

export type FormStep = (typeof FORM_STEPS)[number];

export const STEP_LABEL_KEY: Record<FormStep, string> = {
  visit: 'guaranteeV2.label.stepVisit',
  details: 'guaranteeV2.label.stepDetails',
  prescriptions: 'guaranteeV2.label.stepPrescriptions',
};

export const STEP_HINT_KEY: Record<FormStep, string> = {
  visit: 'guaranteeV2.message.stepVisitHint',
  details: 'guaranteeV2.message.stepDetailsHint',
  prescriptions: 'guaranteeV2.message.stepPrescriptionsHint',
};

/**
 * Assembles a guarantee: which visit it answers, what it states, then what it pays for.
 *
 * The visit settles the rest — with an appointment it is a doctor's care plan, without one it is the
 * client's own request, which is where `isServiceRequest` comes from. An appointment's medical data
 * seeds step two. Amending re-enters at the first step still incomplete.
 */
@Component({
  selector: 'guarantees-guarantee-form',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    Icd10PickerComponent,
    AppointmentPickerComponent,
    ControlDirective,
    CalloutComponent,
    CheckBoxComponent,
    ChipComponent,
    DiagnosisChipComponent,
    FieldLabelComponent,
    OptionPickerComponent,
    ToggleDirective,
    StepNavComponent,
  ],
  templateUrl: './guarantee-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuaranteeFormComponent {
  /** Heading, scrolling body and pinned acts — the one dialog shape this module uses. */
  protected readonly layout = DIALOG_CLASSES;

  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;

  private readonly dialogRef = inject(MatDialogRef<GuaranteeFormComponent, GuaranteeFormResult>);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  protected readonly data = inject<GuaranteeFormData>(MAT_DIALOG_DATA);
  protected readonly isCreate = this.data.mode === 'create';

  private readonly visits = this.store.selectSignal(selectTimelineVisits);
  private readonly liveContext = this.store.selectSignal(selectLiveRefreshContext);

  /** The write already in flight — the effect takes one create at a time, and this is its gate. */
  protected readonly saving = this.store.selectSignal(selectGuaranteesSaving);

  /** The route as the nav reads it: what each step is called and whether it has been answered. */
  protected readonly stepEntries = computed<StepNavEntry[]>(() => FORM_STEPS.map(id => ({ id, labelKey: STEP_LABEL_KEY[id], completed: this.completed()[id] })));
  protected readonly stepHintKey = STEP_HINT_KEY;
  protected readonly roles = DiagnosisRole;
  protected readonly kindIcon = SERVICE_KIND_ICON;
  protected readonly kindLabelKey = SERVICE_KIND_LABEL_KEY;
  protected readonly answers = DECISION_ANSWERS;
  /** Named so the template can tell the one answer a line can be refused for apart from the other. */
  protected readonly guaranteed = GuaranteeDecision.Guaranteed;
  protected readonly decisionIcon = DECISION_ICON;
  protected readonly decisionLabelKey = DECISION_LABEL_KEY;

  protected readonly appointmentId = signal(this.data.initial?.appointmentId);
  protected readonly description = signal(this.data.initial?.description ?? '');
  protected readonly diagnoses = signal<DiagnosisDraft[]>([]);

  /** Held, not written one by one: create is a single transactional write. */
  protected readonly lines = signal<QueuedLine[]>([]);

  /** Each add remounts the picker, so it comes back empty rather than holding the last pick. */
  protected readonly pickerKey = signal(0);

  /** What the picker must not offer again: a guarantee names each code once, or the care plan splits. */
  protected readonly takenDiagnosisCodes = computed(() => this.diagnoses().map(draft => draft.coding.code ?? ''));

  /**
   * What the client asked for themselves, rather than what a doctor prescribed at a visit. No visit
   * behind the guarantee makes it one, whichever step of the form the answer came from: choosing
   * "without a visit" on an amendment is a manager saying exactly that.
   *
   * The reverse does not hold, so the stored answer is carried rather than re-derived — booking a
   * visit against a request does not turn it into something a doctor ordered.
   */
  protected readonly isServiceRequest = computed(() => !this.appointmentId() || !!this.data.initial?.isServiceRequest);

  /**
   * Guarantees already raised on the chosen visit. Offered while raising one, so a visit that is
   * already answered is opened rather than answered twice; amending is already inside one of them.
   */
  protected readonly existingGuarantees = computed(() => {
    const appointmentId = this.appointmentId();

    return this.isCreate && appointmentId ? this.visits().filter(visit => visit.appointmentId === appointmentId) : [];
  });

  /**
   * Why a ruling given here could not be sent as it stands, if any could not. Two things stop one:
   * grounds not yet chosen, and a grant on a line the ruling flow would refuse anyway — a line with
   * no diagnosis behind it, or none the dictionary can match.
   *
   * The check is the ruling flow's own {@link allowsGrant}, not a second reading of it: a form that
   * allowed what the flow refuses would be the way around the rule rather than a restatement of it.
   *
   * Declared above {@link completed}, which reads it: `step` below evaluates `completed()` while
   * the fields are still initialising, and a computed declared later than that is not yet a
   * function when it is reached.
   */
  protected readonly decisionBlockedKey = computed(() => {
    for (const entry of this.lines()) {
      if (!entry.decision) {
        continue;
      }

      if (entry.decision.answer === GuaranteeDecision.Guaranteed && this.grantBlockerKey(entry.prescription)) {
        return 'guaranteeV2.message.draftDecisionBlocked';
      }

      if (!entry.decision.reason.trim()) {
        return 'guaranteeV2.message.decisionReasonRequired';
      }
    }

    return '';
  });

  /**
   * A step counts as done once it holds what the next step needs; only the first is required. Read
   * off what the form is holding now, since that is what the stepper is showing — the step it opens
   * on comes from here too, and a second rule for that would let the two contradict each other.
   *
   * An amendment edits the description alone in step two: its diagnoses are added from the card and
   * its lines from the care plan, so the drafts counted here only ever fill while raising one.
   */
  protected readonly completed = computed<Record<FormStep, boolean>>(() => ({
    visit: !!this.appointmentId() || this.visitSkipped(),
    details: this.isCreate ? this.diagnoses().length > 0 : !!this.description().trim(),
    prescriptions: this.isCreate ? this.lines().length > 0 && !this.decisionBlockedKey() : (this.data.prescriptionCount ?? 0) > 0,
  }));

  /** True on every amendment without a visit, or the form traps on a question already answered. */
  protected readonly visitSkipped = signal(!this.isCreate && !this.data.initial?.appointmentId);

  /** Opens on the first step still owed an answer, and at the beginning once none are. */
  protected readonly step = signal<FormStep>(FORM_STEPS.find(step => !this.completed()[step]) ?? 'visit');

  protected readonly stepIndex = computed(() => FORM_STEPS.indexOf(this.step()));
  protected readonly isLast = computed(() => this.stepIndex() === FORM_STEPS.length - 1);
  protected readonly isFirst = computed(() => this.stepIndex() === 0);

  /** Step one has to be answered — either an appointment or a stated decision to raise without one. */
  protected readonly canLeaveVisit = computed(() => !!this.appointmentId() || this.visitSkipped());

  protected readonly canSubmit = computed(() => this.canLeaveVisit() && !this.decisionBlockedKey());

  /** Names the one thing still owed, so the disabled button says what to go and do. */
  protected readonly submitBlockedKey = computed(() => {
    if (!this.canLeaveVisit()) {
      return 'guaranteeV2.message.stepVisitRequired';
    }

    return this.decisionBlockedKey();
  });

  /**
   * Whether the review may be closed as part of raising the record — the same rule the visit header
   * applies afterwards: nothing still owed an answer. A record with no lines is not offered it, or
   * "nothing undecided" would be true of a guarantee that decides nothing.
   */
  protected readonly canMarkProcessed = computed(() => this.isCreate && this.lines().length > 0 && this.lines().every(entry => !!entry.decision) && !this.decisionBlockedKey());

  /**
   * Closing the review as part of the create. Tied to the gate rather than held beside it: taking a
   * ruling back off a line drops the intention with it, so the form cannot submit an act it is no
   * longer offering.
   */
  protected readonly markProcessed = linkedSignal<boolean, boolean>({
    source: this.canMarkProcessed,
    computation: (allowed, previous) => allowed && (previous?.value ?? false),
  });

  protected readonly canAdvance = computed(() => (this.step() === 'visit' ? this.canLeaveVisit() : true));

  protected goTo(step: FormStep): void {
    this.step.set(step);
  }

  protected next(): void {
    const index = this.stepIndex();
    if (index < FORM_STEPS.length - 1 && this.canAdvance()) {
      this.step.set(FORM_STEPS[index + 1]);
    }
  }

  protected back(): void {
    const index = this.stepIndex();
    if (index > 0) {
      this.step.set(FORM_STEPS[index - 1]);
    }
  }

  /**
   * Seeds the appointment's own diagnoses, keeping anything already picked. They arrive as primary,
   * like every code on the list: the visit recorded them as what it was for, and demoting one is
   * the manager's to do rather than the form's to assume.
   */
  protected chooseAppointment(choice: AppointmentChoice): void {
    this.appointmentId.set(choice.id);
    this.visitSkipped.set(false);

    const known = new Set(this.diagnoses().map(draft => draft.coding.code));
    const seeded = choice.diagnoses.filter(coding => !known.has(coding.code));
    if (seeded.length > 0) {
      this.diagnoses.update(current => [...current, ...seeded.map(coding => ({ coding, role: DiagnosisRole.Primary }))]);
    }
  }

  protected clearAppointment(): void {
    this.appointmentId.set(undefined);
  }

  protected skipVisit(): void {
    this.clearAppointment();
    this.visitSkipped.set(true);
  }

  /**
   * The code joins the list as primary, which is what a code picked for a guarantee usually is. The
   * ones that are not are demoted in place, so the common answer costs nothing and the exception is
   * still stated rather than derived from the order they were typed in.
   */
  protected addDiagnosis(coding: CodingModel): void {
    if (this.diagnoses().some(draft => draft.coding.code === coding.code)) {
      return;
    }

    this.diagnoses.update(current => [...current, { coding, role: DiagnosisRole.Primary }]);
    this.pickerKey.update(key => key + 1);
  }

  /**
   * As many primaries as the visit actually had: a guarantee answering for two conditions at once
   * names both, and the one the timeline groups the record under is simply the first of them.
   */
  protected setRole(code: string, role: DiagnosisRole): void {
    this.diagnoses.update(current => current.map(draft => (draft.coding.code === code ? { ...draft, role } : draft)));
  }

  /**
   * Taking a code off the guarantee takes it off the lines that named it, and re-reads what those
   * lines were ruled: the queue is written in one transaction, so a link and a ruling left standing
   * on a code that is gone would be created against nothing.
   *
   * Only removal can do this. A code added takes nothing away from a line that named another, and a
   * line naming none is not waiting on one — it was left without deliberately.
   */
  protected removeDiagnosis(code: string): void {
    this.diagnoses.update(current => current.filter(draft => draft.coding.code !== code));

    this.lines.update(current =>
      current.map(entry => this.reconciled(entry.prescription.diagnosisId === code ? { ...entry, prescription: { ...entry.prescription, diagnosisId: undefined } } : entry))
    );
  }

  /**
   * A queued line's ruling read against the guarantee as it now stands. Cover is granted on a
   * diagnosis and on a service the dictionary knows, so a grant survives only while both still hold:
   * a diagnosis taken off the guarantee, or a line re-authored as something unmapped, leaves an
   * answer the record could not carry, and the line goes back to undecided. A refusal is never
   * disturbed — refusing cover needs neither of them.
   */
  private reconciled(entry: QueuedLine): QueuedLine {
    if (entry.decision?.answer !== GuaranteeDecision.Guaranteed || !this.grantBlockerKey(entry.prescription)) {
      return entry;
    }

    return { prescription: entry.prescription };
  }

  /** The guarantee does not exist yet, so the form answers with the record and it is queued here. */
  protected addPrescription(index?: number): void {
    const queued = index === undefined ? undefined : this.lines()[index];

    // `draft` mode: the line was never written, so no edit reason is asked for.
    const data: PrescriptionFormData = {
      mode: index === undefined ? 'create' : 'draft',
      initial: queued?.prescription,
      // Keyed by ICD-10 code, not id: these diagnoses do not exist yet. The code is unique here —
      // step two refuses a second draft of the same one — and the submit mints the ids behind it.
      diagnoses: this.diagnoses().map<DiagnosisChipView>(draft => ({ id: draft.coding.code ?? '', role: draft.role, icd10: draft.coding })),
      // Ruled where it is written: a manager adding several lines answers each one in the dialog
      // they are already in, rather than returning to the queue to rule on all of them afterwards.
      collectsDecision: true,
      decision: queued?.decision,
      memberUserId: this.liveContext().memberUserId,
    };

    this.dialog
      .open<PrescriptionFormComponent, PrescriptionFormData, PrescriptionFormResult>(PrescriptionFormComponent, { ...PRESCRIPTION_FORM_DIALOG_CONFIG, data })
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (!result) {
          return;
        }

        // The ruling comes back from the form, which opened holding whatever the line was already
        // being given — so grounds already chosen are never re-asked. Still reconciled here: the
        // guarantee's diagnoses can have moved while the dialog was open.
        const line: QueuedLine = { prescription: result.prescription, decision: result.decision };

        this.lines.update(current =>
          index === undefined ? [...current, this.reconciled(line)] : current.map((entry, position) => (position === index ? this.reconciled(line) : entry))
        );
      });
  }

  protected removePrescription(index: number): void {
    this.lines.update(current => current.filter((_, position) => position !== index));
  }

  /**
   * The grounds each answer may be given on, held once per answer rather than built per row: the
   * picker reads its options by identity, and a fresh array each cycle would rebuild every list on
   * screen for a question nobody asked.
   */
  private readonly reasonsByAnswer: Record<DecisionAnswer, PickerOption[]> = {
    [GuaranteeDecision.Guaranteed]: GUARANTEE_REASONS.map(value => ({ value })),
    [GuaranteeDecision.NotGuaranteed]: DECLINE_REASONS.map(value => ({ value })),
  };

  protected reasonOptions(answer: DecisionAnswer): PickerOption[] {
    return this.reasonsByAnswer[answer];
  }

  protected reasonPlaceholderKey(answer: DecisionAnswer): string {
    return answer === GuaranteeDecision.Guaranteed ? 'guaranteeV2.message.guaranteeReasonPlaceholder' : 'guaranteeV2.message.declineReasonPlaceholder';
  }

  /**
   * Which of the guarantee's diagnoses a queued line answers: the code it named, and none where it
   * named none. A sole diagnosis is adopted once, by the form that authors the line, so a line
   * carrying none is one somebody left without — and the queue says so rather than filing it under
   * whichever code happens to be the only one left.
   */
  private diagnosisBehind(prescription: PrescriptionWriteModel): string | undefined {
    // Matched against the list as it stands rather than trusted off the line: a code no longer on
    // the guarantee pairs with nothing, so a line still naming one is a line with no diagnosis.
    return this.diagnoses().find(draft => draft.coding.code === prescription.diagnosisId)?.coding.code;
  }

  /**
   * The queue split the way the care plan splits a stored guarantee: each diagnosis over the lines
   * it answers, and anything with none behind it last — where a line sits until somebody says what
   * it is prescribed against, since cover cannot be granted on it there.
   *
   * A diagnosis nothing is prescribed against is left out — step two already lists it, and an empty
   * group here would say the care plan is short of something it is not.
   */
  protected readonly queuedGroups = computed<QueuedGroup[]>(() => {
    const entries = this.lines().map((queued, index) => ({ index, queued, code: this.diagnosisBehind(queued.prescription) }));

    const groups = this.diagnoses()
      .map<QueuedGroup>(draft => ({
        key: draft.coding.code ?? '',
        diagnosis: { role: draft.role, icd10: draft.coding },
        entries: entries.filter(entry => entry.code === draft.coding.code),
      }))
      .filter(group => group.entries.length > 0);

    const unassigned = entries.filter(entry => !entry.code);

    return unassigned.length > 0 ? [...groups, { key: '', diagnosis: null, entries: unassigned }] : groups;
  });

  /** Why cover cannot be granted on this line as authored, in the words the ruling flow uses. */
  protected grantBlockerKey(prescription: PrescriptionWriteModel): string {
    const diagnosisId = this.diagnosisBehind(prescription);
    if (!allowsGrant({ ...prescription, diagnosisId })) {
      return DECISION_BLOCKER_HINT_KEY[diagnosisId ? DecisionBlocker.Unmapped : DecisionBlocker.NoDiagnosis];
    }

    return hasCategories(prescription) ? '' : DECISION_BLOCKER_HINT_KEY[DecisionBlocker.NoCategories];
  }

  /**
   * Ruling on a queued line, or taking the ruling back off it. Grounds belong to the answer that was
   * given, so choosing a different one asks for them again rather than carrying a refusal's wording
   * onto a grant — the same rule the ruling flow applies.
   */
  protected setDecision(index: number, answer?: DecisionAnswer): void {
    this.lines.update(current =>
      current.map((entry, position) => {
        if (position !== index) {
          return entry;
        }

        if (!answer) {
          return { prescription: entry.prescription };
        }

        return entry.decision?.answer === answer ? entry : { prescription: entry.prescription, decision: { answer, reason: '' } };
      })
    );
  }

  protected setReason(index: number, reason: string): void {
    this.lines.update(current => current.map((entry, position) => (position === index && entry.decision ? { ...entry, decision: { ...entry.decision, reason } } : entry)));
  }

  protected submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    const description = this.description().trim();
    const header = {
      appointmentId: this.appointmentId(),
      description: description || undefined,
      isServiceRequest: this.isServiceRequest(),
      isLiloStaff: this.data.initial?.isLiloStaff ?? false,
    };

    if (!this.isCreate) {
      this.dialogRef.close({ kind: 'submit', request: header satisfies UpdateGuaranteeRequest });

      return;
    }

    // Minted here so a line can name its diagnosis before the record exists: the queue keys them by
    // ICD-10 code, which is unique on the form but is not an id the service would accept.
    const diagnosisIdByCode = new Map(this.diagnoses().map(draft => [draft.coding.code, crypto.randomUUID()]));

    this.dialogRef.close({
      kind: 'submit',
      request: {
        ...header,
        diagnoses: this.diagnoses().map<DiagnosisWriteModel>(draft => ({
          id: diagnosisIdByCode.get(draft.coding.code),
          role: draft.role,
          icd10Code: draft.coding.code,
          authorType: DiagnosisAuthorType.HealthManager,
        })),
        prescriptions: this.lines().map<PrescriptionWriteModel>(entry => ({
          ...entry.prescription,
          diagnosisId: diagnosisIdByCode.get(this.diagnosisBehind(entry.prescription)),
          decision: toDecisionWriteModel(entry.decision),
          // The option block goes with a grant only; a refusal or an unruled line carries none.
          coverage: entry.decision?.answer === GuaranteeDecision.Guaranteed ? entry.prescription.coverage : undefined,
        })),
        markProcessed: this.markProcessed(),
      } satisfies CreateGuaranteeRequest,
    });
  }

  /** Leaving for a guarantee the visit already has, instead of raising a second one beside it. */
  protected openExisting(guaranteeId: string): void {
    this.dialogRef.close({ kind: 'open', guaranteeId });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }
}
